'use client'

import { useEffect } from 'react'
import {
  Text,
  Bubble,
  Cell,
  Spacer,
  Navigation,
  Button,
  Dialog,
  Divider,
  Icon,
  Image,
} from '@vinted/web-ui'
import { Download24, QrCode24, X24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { UnknownErrorModal } from '@marketplace-web/pay-identity/pay-identity-kyc-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import {
  downloadBase64File,
  downloadPickupLabel,
  transformPickUpParcelCodeResponse,
  getPickUpParcelCode,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'

type Props = {
  isOpen: boolean
  onClose: () => void
  transactionId?: number
}

const PickUpParcellModal = ({ isOpen, onClose, transactionId }: Props) => {
  const translate = useTranslate('conversation.pickup_code.modal')

  const {
    fetch: fetchPickUpCode,
    transformedData: pickUpCode,
    isLoading,
    error,
  } = useFetch(getPickUpParcelCode, response => transformPickUpParcelCodeResponse(response))

  useEffect(() => {
    if (!transactionId || !isOpen) return

    fetchPickUpCode({ transactionId })
  }, [transactionId, isOpen, fetchPickUpCode])

  function renderQrCode() {
    return (
      <Cell>
        <div className="u-flexbox u-flex-direction-column u-align-items-center">
          <Text
            as="h1"
            type={Text.Type.Heading}
            text={translate('title')}
            alignment={Text.Alignment.Center}
          />
          <div className="pickup-code-modal__image">
            <Text
              text={translate('subtitle')}
              alignment={Text.Alignment.Center}
              html
              testId="pickup-code-modal-header-subtitle"
              as="span"
            />
            <Spacer size={Spacer.Size.Large} />
            <Bubble styling={Bubble.Styling.Tight}>
              <Image
                src={pickUpCode?.imageUrl}
                scaling={Image.Scaling.Cover}
                testId="pickup-code-modal-image"
              />
            </Bubble>
            <Spacer size={Spacer.Size.Large} />
          </div>
          <Text as="span" text={translate('code_title')} alignment={Text.Alignment.Center} />
          <Text
            as="h1"
            type={Text.Type.Heading}
            text={pickUpCode?.code}
            alignment={Text.Alignment.Center}
          />
        </div>
      </Cell>
    )
  }

  async function handleDownloadQrCodeClick() {
    if (!transactionId) return

    const response = await downloadPickupLabel(transactionId)

    if ('errors' in response) return

    const base64Data = response.file.parcel_collection_code
    const fileName = response.file.filename

    downloadBase64File({ base64Data, fileName })
  }

  function renderDownloadQrCode() {
    return (
      <Cell
        clickable
        prefix={<Icon name={QrCode24} color={Icon.Color.GreyscaleLevel2} />}
        title={translate('donwload_qr_code')}
        suffix={<Icon name={Download24} color={Icon.Color.GreyscaleLevel1} />}
        onClick={handleDownloadQrCodeClick}
      />
    )
  }

  if (error) return <UnknownErrorModal show onSubmit={onClose} />

  const renderLoader = () => (
    <ContentLoader styling={ContentLoader.Styling.Wide} testId="pickup-parcel-modal-loader" />
  )

  function renderContent() {
    if (isLoading) return renderLoader()

    return (
      <>
        <Navigation
          body={translate('header')}
          right={
            <Button
              styling={Button.Styling.Flat}
              onClick={onClose}
              iconName={X24}
              testId="close-pickup-parcel-modal"
              inline
            />
          }
        />
        <Divider />
        <ScrollableArea>
          {renderQrCode()}
          <SeparatedList separator={<Divider />}>
            {renderDownloadQrCode()}
            <Spacer />
          </SeparatedList>
        </ScrollableArea>
      </>
    )
  }

  return (
    <Dialog show={isOpen} testId="pickup-parcel-modal">
      {renderContent()}
    </Dialog>
  )
}

export default PickUpParcellModal
